import React, { useState, useRef } from 'react';
import API_BASE_URL from '../../config';

const IDEA_STAGES = [
  'Just an idea',
  'Basic research done',
  'Prototype ready',
  'Already working on it',
];

export default function PublicPaidRegistrationForm() {
  const REGISTRATION_AMOUNT = 299;
  const UPI_ID = 'suneelpulijala-1@oksbi';
  const PAYEE_NAME = 'suneel pulijala';
  const upiUri = `upi://pay?pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent(PAYEE_NAME)}&am=${REGISTRATION_AMOUNT}&cu=INR&tn=${encodeURIComponent('Team Registration')}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(upiUri)}`;

  const [form, setForm] = useState({
    teamName: '',
    teamMembers: '1',
    teamLeaderName: '',
    mobileNumber: '',
    emailAddress: '',
    address: '',
    linkedInUrl: '',
    instagramUrl: '',
    secondTeammateName: '',
    secondTeammateEmail: '',
    secondTeammatePhone: '',
    secondTeammateLinkedInUrl: '',
    secondTeammateInstagramUrl: '',
    ideaStage: '',
    feeConfirmed: false,
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const paymentScreenshotInputRef = useRef(null);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitWithPayment = async (e) => {
    e.preventDefault();
    setStatus('');

    if (!form.teamName || !form.teamLeaderName || !form.mobileNumber || !form.emailAddress || !form.address || !form.ideaStage) {
      setStatus('Please fill all required fields.');
      return;
    }
    if (
      Number(form.teamMembers) === 2 &&
      (!form.secondTeammateName || !form.secondTeammateEmail || !form.secondTeammatePhone)
    ) {
      setStatus('Please fill second teammate name, email, and phone.');
      return;
    }
    if (!form.feeConfirmed) {
      setStatus(`Please confirm the ₹${REGISTRATION_AMOUNT} registration fee.`);
      return;
    }
    setShowPaymentStep(true);
  };

  const submitAfterPayment = async () => {
    setStatus('');
    if (!paymentReference.trim()) {
      setStatus('Please enter UPI transaction/reference number after payment.');
      return;
    }
    if (!paymentScreenshot || !paymentScreenshot.size) {
      setStatus('Payment screenshot is required. Please upload a clear photo of your payment success screen.');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('teamName', form.teamName);
      fd.append('teamMembers', String(form.teamMembers));
      fd.append('teamLeaderName', form.teamLeaderName);
      fd.append('mobileNumber', form.mobileNumber);
      fd.append('emailAddress', form.emailAddress);
      fd.append('address', form.address);
      fd.append('linkedInUrl', form.linkedInUrl || '');
      fd.append('instagramUrl', form.instagramUrl || '');
      fd.append('secondTeammateName', form.secondTeammateName || '');
      fd.append('secondTeammateEmail', form.secondTeammateEmail || '');
      fd.append('secondTeammatePhone', form.secondTeammatePhone || '');
      fd.append('secondTeammateLinkedInUrl', form.secondTeammateLinkedInUrl || '');
      fd.append('secondTeammateInstagramUrl', form.secondTeammateInstagramUrl || '');
      fd.append('ideaStage', form.ideaStage);
      fd.append('feeConfirmed', form.feeConfirmed ? 'true' : 'false');
      fd.append('amountPaid', String(REGISTRATION_AMOUNT));
      fd.append('paymentReference', paymentReference.trim());
      fd.append('paymentMethod', 'UPI_QR');
      fd.append('paymentScreenshot', paymentScreenshot, paymentScreenshot.name || 'payment-screenshot.jpg');

      const submitRes = await fetch(`${API_BASE_URL}/api/public-team-registrations`, {
        method: 'POST',
        body: fd,
      });
      const submitData = await submitRes.json().catch(() => ({}));
      if (!submitRes.ok) {
        setStatus(submitData.error || submitData.details || 'Registration failed. Please try again.');
        return;
      }
      setStatus('Registration submitted. Payment will be verified by our team.');
      setShowPaymentStep(false);
      setPaymentReference('');
      setPaymentScreenshot(null);
      if (paymentScreenshotInputRef.current) {
        paymentScreenshotInputRef.current.value = '';
      }
      setForm({
        teamName: '',
        teamMembers: '1',
        teamLeaderName: '',
        mobileNumber: '',
        emailAddress: '',
        address: '',
        linkedInUrl: '',
        instagramUrl: '',
        secondTeammateName: '',
        secondTeammateEmail: '',
        secondTeammatePhone: '',
        secondTeammateLinkedInUrl: '',
        secondTeammateInstagramUrl: '',
        ideaStage: '',
        feeConfirmed: false,
      });
    } catch (_err) {
      setStatus('Payment completed but registration save failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="public-paid-form-wrap">
      <h2>Team Registration (Public)</h2>
      <p>Open to all. Complete payment to submit registration.</p>

      <form className="public-paid-form" onSubmit={submitWithPayment}>
        <label>
          Team Name *
          <input name="teamName" value={form.teamName} onChange={onChange} />
        </label>

        <label>
          Number of Team Members *
          <select name="teamMembers" value={form.teamMembers} onChange={onChange}>
            <option value="1">1</option>
            <option value="2">2</option>
          </select>
        </label>

        <label>
          Team Leader Name *
          <input name="teamLeaderName" value={form.teamLeaderName} onChange={onChange} />
        </label>

        <label>
          Mobile Number *
          <input name="mobileNumber" value={form.mobileNumber} onChange={onChange} />
        </label>

        <label>
          Email Address *
          <input type="email" name="emailAddress" value={form.emailAddress} onChange={onChange} />
        </label>

        <label>
          Address (City / College / Organization) *
          <input name="address" value={form.address} onChange={onChange} />
        </label>

        <label>
          LinkedIn Profile URL
          <input name="linkedInUrl" value={form.linkedInUrl} onChange={onChange} />
        </label>

        <label>
          Instagram Profile URL
          <input name="instagramUrl" value={form.instagramUrl} onChange={onChange} />
        </label>

        {Number(form.teamMembers) === 2 && (
          <>
            <label>
              2nd Team Mate Name *
              <input name="secondTeammateName" value={form.secondTeammateName} onChange={onChange} />
            </label>
            <label>
              2nd Team Mate Email *
              <input type="email" name="secondTeammateEmail" value={form.secondTeammateEmail} onChange={onChange} />
            </label>
            <label>
              2nd Team Mate Phone Number *
              <input name="secondTeammatePhone" value={form.secondTeammatePhone} onChange={onChange} />
            </label>
            <label>
              2nd Team Mate LinkedIn Profile URL
              <input name="secondTeammateLinkedInUrl" value={form.secondTeammateLinkedInUrl} onChange={onChange} />
            </label>
            <label>
              2nd Team Mate Instagram Profile URL
              <input name="secondTeammateInstagramUrl" value={form.secondTeammateInstagramUrl} onChange={onChange} />
            </label>
          </>
        )}

        <label>
          What stage is your idea currently in? *
          <select name="ideaStage" value={form.ideaStage} onChange={onChange}>
            <option value="">-- Select --</option>
            {IDEA_STAGES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </label>

        <label className="public-paid-checkbox">
          <input type="checkbox" name="feeConfirmed" checked={form.feeConfirmed} onChange={onChange} />
          <span>Registration Fee Confirmation: I agree to pay ₹{REGISTRATION_AMOUNT} to complete my registration</span>
        </label>

        <button type="submit" disabled={loading}>
          Continue to Pay ₹{REGISTRATION_AMOUNT}
        </button>
      </form>

      {showPaymentStep && (
        <div className="manual-payment-box">
          <h3>Pay ₹{REGISTRATION_AMOUNT} using UPI QR</h3>
          <img src={qrUrl} alt="UPI payment QR code" className="manual-payment-qr" />
          <p><strong>Name:</strong> {PAYEE_NAME}</p>
          <p><strong>UPI ID:</strong> {UPI_ID}</p>
          <label>
            UPI Transaction / Reference Number *
            <input
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Enter UPI reference number"
            />
          </label>
          <label className="manual-payment-screenshot">
            <span className="manual-payment-screenshot-title">
              Payment screenshot (required) *
            </span>
            <span className="manual-payment-screenshot-hint">
              Upload a clear screenshot of your paid transaction (Google Pay / PhonePe / Paytm success screen).
              JPG, PNG, or WebP — max 10 MB.
            </span>
            <input
              ref={paymentScreenshotInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
              capture="environment"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                setPaymentScreenshot(f || null);
              }}
            />
          </label>
          <div className="manual-payment-actions">
            <button type="button" onClick={submitAfterPayment} disabled={loading}>
              {loading ? 'Submitting...' : 'I Have Paid - Submit Registration'}
            </button>
            <button
              type="button"
              className="manual-payment-cancel"
              onClick={() => {
                setShowPaymentStep(false);
                setPaymentScreenshot(null);
                if (paymentScreenshotInputRef.current) {
                  paymentScreenshotInputRef.current.value = '';
                }
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {status && <p className="public-paid-status">{status}</p>}

      <div className="public-paid-thankyou">
        <p><strong>🎉 Thank You for Registering!</strong></p>
        <p>Please join our WhatsApp group for further updates regarding the event.</p>
        <p>
          <a
            href="https://chat.whatsapp.com/GrjTutjcT5oD2mXIDwk3nj"
            target="_blank"
            rel="noreferrer"
          >
            https://chat.whatsapp.com/GrjTutjcT5oD2mXIDwk3nj
          </a>
        </p>
        <p>
          Our team will review your payment and registration shortly and confirm your slot.
          You will also receive a final confirmation email from our team soon! ✨
        </p>
      </div>
    </section>
  );
}
